import React, { useEffect, useState } from "react";
import { isOnSale } from "../context/CartContext";
import api from "../lib/api";

// Admin "Show Afterpay badge" display flag — cached once per page load, hidden by default.
let _displayPromise = null;
const useShowAfterpayBadge = () => {
    const [show, setShow] = useState(false);
    useEffect(() => {
        if (!_displayPromise) {
            _displayPromise = api.get("/site/display")
                .then(({ data }) => !!data?.show_afterpay_badge)
                .catch(() => false);
        }
        let alive = true;
        _displayPromise.then((v) => { if (alive) setShow(v); });
        return () => { alive = false; };
    }, []);
    return show;
};

/** Renders price with sale strikethrough + percent saved, used in cards/PDP/related/cart. */
export const PriceDisplay = ({ product, size = "md", align = "left" }) => {
    const onSale = isOnSale(product);
    const sale = Number(product.sale_price || 0);
    const rrp = Number(product.price || 0);
    const pct = onSale && rrp > 0 ? Math.round(((rrp - sale) / rrp) * 100) : 0;

    const priceCls = {
        sm: "text-base",
        md: "text-xl",
        lg: "text-3xl",
        xl: "text-4xl md:text-5xl",
    }[size] || "text-xl";
    const rrpCls = {
        sm: "text-xs",
        md: "text-sm",
        lg: "text-base",
        xl: "text-lg",
    }[size] || "text-sm";

    return (
        <div
            className={`inline-flex items-baseline gap-2 ${align === "right" ? "justify-end" : ""}`}
            data-testid={`price-${product.slug}`}
        >
            <span className={`font-display ${priceCls} font-bold ${onSale ? "text-[var(--gold)]" : "text-white"}`} data-testid={`price-current-${product.slug}`}>
                A${(onSale ? sale : rrp).toFixed(2)}
            </span>
            {onSale && (
                <>
                    <span
                        className={`font-mono ${rrpCls} text-[var(--text-muted)] line-through`}
                        data-testid={`price-rrp-${product.slug}`}
                    >
                        A${rrp.toFixed(2)}
                    </span>
                    <span
                        className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest border border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                        data-testid={`price-save-${product.slug}`}
                    >
                        Save {pct}%
                    </span>
                </>
            )}
        </div>
    );
};

/** SALE badge for product imagery. */
export const SaleBadge = ({ product, className = "" }) => {
    if (!isOnSale(product)) return null;
    const rrp = Number(product.price || 0);
    const sale = Number(product.sale_price || 0);
    const pct = rrp > 0 ? Math.round(((rrp - sale) / rrp) * 100) : 0;
    return (
        <span
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono font-bold uppercase tracking-widest bg-gradient-to-r from-[#E9BC0B] to-[#F5C932] text-black shadow-lg ${className}`}
            data-testid={`sale-badge-${product.slug}`}
        >
            Sale −{pct}%
        </span>
    );
};

// BNPL provider specs (AUD, single-transaction rules that mirror Stripe's own
// eligibility — outside these bands Stripe hides the method at checkout, so
// we hide the marketing copy too rather than promise a plan we can't deliver).
const BNPL_PROVIDERS = [
    {
        key: "afterpay",
        label: "Afterpay",
        color: "#B2FCE4",
        min: 1, max: 2000, installments: 4,
        template: (amount) => `4 fortnightly payments of A$${(amount / 4).toFixed(2)}`,
    },
    {
        key: "zip",
        label: "Zip",
        color: "#AA8FFF",
        min: 1, max: 1000, installments: 6,
        template: (amount) => `6 weekly payments of A$${(amount / 6).toFixed(2)}`,
    },
    {
        key: "klarna",
        label: "Klarna",
        color: "#FFA8CD",
        min: 1, max: 1000, installments: 3,
        template: (amount) => `Pay in 3 of A$${(amount / 3).toFixed(2)}`,
    },
];

const _effectivePrice = (product) => {
    const rrp = Number(product?.price || 0);
    const sale = Number(product?.sale_price || 0);
    return isOnSale(product) && sale > 0 ? sale : rrp;
};

/** Compact single-badge BNPL availability indicator for product cards.
 * Renders one small pill: "AFTERPAY AVAILABLE" when Afterpay's price band matches,
 * otherwise "BNPL AVAILABLE" if any other provider fits, otherwise nothing.
 * Does NOT affect provider eligibility or Stripe checkout behaviour — informational only. */
export const BnplBadge = ({ product, className = "" }) => {
    const show = useShowAfterpayBadge();
    if (!show) return null;
    const value = _effectivePrice(product);
    if (!Number.isFinite(value) || value <= 0) return null;

    const eligible = BNPL_PROVIDERS.filter((p) => value >= p.min && value <= p.max);
    if (eligible.length === 0) return null;

    const hasAfterpay = eligible.some((p) => p.key === "afterpay");
    const label = hasAfterpay ? "Afterpay Available" : "BNPL Available";

    return (
        <span
            className={`inline-flex items-center gap-1 self-start px-1.5 py-0.5 rounded-sm text-[8.5px] font-mono font-semibold uppercase tracking-[0.12em] whitespace-nowrap border border-[rgba(56,214,232,0.35)] bg-[rgba(56,214,232,0.06)] text-[var(--cyan)] ${className}`}
            data-testid={`bnpl-badge-${product?.slug || "product"}`}
        >
            <span
                aria-hidden="true"
                className="inline-block w-1 h-1 rounded-full bg-[var(--cyan)] shadow-[0_0_6px_var(--cyan)]"
            />
            {label}
        </span>
    );
};

/** Product-based BNPL stack — shows every eligible provider under the price.
 * Amount reflects the SALE price when a sale is active (matches Stripe checkout).
 * VIP tier discounts are applied at cart, not shown here — the provider will
 * quote against whatever Stripe finally charges. */
export const BnplNotice = ({ product, quantity = 1, size = "sm", className = "" }) => {
    const show = useShowAfterpayBadge();
    if (!show) return null;
    const unit = _effectivePrice(product);
    const total = Number(unit || 0) * Math.max(1, Number(quantity) || 1);
    return (
        <BnplAmountNotice
            amount={total}
            size={size}
            className={className}
            testIdSuffix={product?.slug}
        />
    );
};

/** Amount-based BNPL stack — used at cart / checkout where the number reflects
 * the fully discounted grand total, not a per-product price. Renders one line
 * per eligible provider (Afterpay / Zip / Klarna). */
export const BnplAmountNotice = ({ amount, size = "sm", className = "", testIdSuffix = "amount" }) => {
    const value = Number(amount || 0);
    if (!Number.isFinite(value) || value <= 0) return null;

    const eligible = BNPL_PROVIDERS.filter((p) => value >= p.min && value <= p.max);
    if (eligible.length === 0) return null;

    const textCls = size === "md" ? "text-sm" : "text-xs";

    return (
        <div
            className={`flex flex-col gap-0.5 ${textCls} text-[var(--text-muted)] leading-snug ${className}`}
            data-testid={`bnpl-notice-${testIdSuffix}`}
        >
            {eligible.map((p) => (
                <div
                    key={p.key}
                    className="inline-flex flex-wrap items-baseline gap-1"
                    data-testid={`bnpl-line-${p.key}-${testIdSuffix}`}
                >
                    <span>or</span>
                    <span
                        className="font-mono text-white/90 font-semibold"
                        data-testid={`bnpl-instalment-${p.key}-${testIdSuffix}`}
                    >
                        {p.template(value)}
                    </span>
                    <span className="opacity-70">with</span>
                    <span
                        className="font-display font-bold tracking-tight"
                        style={{ color: p.color }}
                        aria-label={p.label}
                        data-testid={`bnpl-brand-${p.key}-${testIdSuffix}`}
                    >
                        {p.label}
                    </span>
                </div>
            ))}
        </div>
    );
};
